import { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { GlobalContext } from '../../contexts/GlobalContext';
import { api } from '../../utils/lib/api';
import { apiRoutes, vars } from '../../config';

// Función para obtener proveedores del usuario con soporte para búsqueda y paginación
export const getMySuppliers = async (entId, userId, options = {}) => {
  try {
    const { limit = 5, offset = 0, searchText = '' } = options;
    let url = `${apiRoutes.providers.base}/${entId}/my-suppliers?userId=${userId}&limit=${limit}&offset=${offset}`;
    
    // Agregar parámetro de búsqueda si existe
    if (searchText && searchText.trim()) {
      url += `&searchText=${encodeURIComponent(searchText.trim())}`;
    }
    
    console.log('URL de my-suppliers:', url);
    const response = await api.get(url);
    return response;
  } catch (error) {
    console.error('Error en getMySuppliers:', error);
    throw error;
  }
};

// Función para obtener datos de un proveedor específico
export const getProviderData = async (entId, odooId) => {
  try {
    const url = `${apiRoutes.providers.base}/${entId}/owner-totals/${odooId}`;
    console.log('URL de owner-totals:', url);
    const response = await api.get(url);
    return response;
  } catch (error) {
    console.error('Error en getProviderData:', error);
    throw error;
  }
};

// Función existente para obtener providers (mantener para compatibilidad)
export const getProviders = async (entId) => {
  try {
    // Usar la configuración correcta de apiRoutes
    const url = `${apiRoutes.providers.base}/${entId}/my-owner-totals`;
    console.log('URL de providers:', url);
    const response = await api.get(url);
    return response;
  } catch (error) {
    console.error('Error en getProviders:', error);
    throw error;
  }
};

// Datos hardcodeados de Venezuela y sus estados
const VENEZUELA_DATA = {
  country: {
    id: 238,
    name: 'Venezuela',
    code: 'VE'
  },
  states: [
    { id: 733, name: 'Amazonas', code: 'AMA' },
    { id: 734, name: 'Anzoátegui', code: 'ANZ' },
    { id: 735, name: 'Apure', code: 'APU' },
    { id: 712, name: 'Aragua', code: 'ARA' },
    { id: 736, name: 'Barinas', code: 'BAR' },
    { id: 737, name: 'Bolívar', code: 'BOL' },
    { id: 738, name: 'Carabobo', code: 'CAR' },
    { id: 739, name: 'Cojedes', code: 'COJ' },
    { id: 740, name: 'Delta Amacuro', code: 'DEL' },
    { id: 710, name: 'Distrito Capital', code: 'DCA' },
    { id: 741, name: 'Falcón', code: 'FAL' },
    { id: 742, name: 'Guárico', code: 'GUA' },
    { id: 743, name: 'Lara', code: 'LAR' },
    { id: 711, name: 'Miranda', code: 'MIR' },
    { id: 744, name: 'Mérida', code: 'MER' },
    { id: 745, name: 'Monagas', code: 'MON' },
    { id: 746, name: 'Nueva Esparta', code: 'NES' },
    { id: 747, name: 'Portuguesa', code: 'POR' },
    { id: 748, name: 'Sucre', code: 'SUC' },
    { id: 749, name: 'Táchira', code: 'TAC' },
    { id: 750, name: 'Trujillo', code: 'TRU' },
    { id: 751, name: 'Vargas', code: 'VAR' },
    { id: 753, name: 'Yaracuy', code: 'YAR' },
    { id: 752, name: 'Zulia', code: 'ZUL' }
  ]
};

// Función para obtener todos los países (ahora retorna solo Venezuela hardcodeado)
export const getCountries = async (entId) => {
  try {
    // Simular respuesta de API con datos hardcodeados de Venezuela
    console.log('Retornando datos hardcodeados de Venezuela');
    return {
      data: {
        statusCode: '10000',
        message: 'Países obtenidos exitosamente',
        data: {
          countries: [VENEZUELA_DATA.country],
          total: 1
        }
      }
    };
  } catch (error) {
    console.error('Error en getCountries:', error);
    throw error;
  }
};

// Función para obtener estados de un país (ahora retorna estados de Venezuela hardcodeados)
export const getStatesByCountry = async (entId, countryId) => {
  try {
    // Simular respuesta de API con datos hardcodeados de estados de Venezuela
    console.log('Retornando datos hardcodeados de estados de Venezuela');
    return {
      data: {
        statusCode: '10000',
        message: 'Estados obtenidos exitosamente',
        data: {
          states: VENEZUELA_DATA.states,
          countryId: 238,
          total: VENEZUELA_DATA.states.length
        }
      }
    };
  } catch (error) {
    console.error('Error en getStatesByCountry:', error);
    throw error;
  }
};

// Función para actualizar el perfil del proveedor
export const updateSupplierProfile = async (entId, supplierId, profileData) => {
  try {
    // Validar que el userId esté presente y sea válido
    const userId = localStorage.getItem('_id');
    if (!userId || userId.trim() === '') {
      console.error('Error: userId no encontrado en localStorage');
      throw new Error('No se encontró el ID del usuario. Por favor, inicia sesión nuevamente.');
    }
    
    console.log('=== DEBUG: updateSupplierProfile ===');
    console.log('entId:', entId);
    console.log('supplierId:', supplierId);
    console.log('userId:', userId);
    console.log('profileData:', profileData);
    
    const url = `${vars.BaseURL}/v1/odoo-owner/${entId}/suppliers/${supplierId}/profile`;
    console.log('URL de updateSupplierProfile:', url);
    
    const response = await api.put(url, profileData);
    return response;
  } catch (error) {
    console.error('Error en updateSupplierProfile:', error);
    // Si el error es sobre el userId, lanzar un error más descriptivo
    if (error.message && error.message.includes('x-user-id')) {
      throw new Error('Error de autenticación: El ID de usuario no es válido. Por favor, inicia sesión nuevamente.');
    }
    throw error;
  }
};

// Nuevo custom hook para manejar providers con contexto global
export const useProviders = () => {
  const globalContext = useContext(GlobalContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0); // Para forzar re-renders
  const [searchText, setSearchText] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const intervalRef = useRef(null);
  const currentEntIdRef = useRef("5f1378dcef2f752aeda16673");
  const hasConfiguredInterval = useRef(false);
  const searchTimeoutRef = useRef(null);
  // Ref para rastrear el ID del request más reciente y evitar que requests antiguos sobrescriban resultados
  const latestRequestIdRef = useRef(0);
  // Ref para rastrear qué estado de carga estableció cada request (para limpiar solo ese estado cuando es obsoleto)
  const requestLoadingStateRef = useRef(new Map());
  
  // Usar refs para mantener referencias estables a las funciones del contexto
  // Esto evita que fetchProviders se recree cuando el contexto cambia
  const contextFunctionsRef = useRef({
    setProvidersData: null,
    setSelectedProviderId: null,
    setProvidersDashboardData: null,
    providersData: null,
    selectedProviderId: null,
    providersDashboardData: null,
  });
  
  // Actualizar refs cuando el contexto cambia (pero no recrear callbacks)
  useEffect(() => {
    contextFunctionsRef.current.setProvidersData = globalContext.setProvidersData;
    contextFunctionsRef.current.setSelectedProviderId = globalContext.setSelectedProviderId;
    contextFunctionsRef.current.setProvidersDashboardData = globalContext.setProvidersDashboardData;
    contextFunctionsRef.current.providersData = globalContext.providersData;
    contextFunctionsRef.current.selectedProviderId = globalContext.selectedProviderId;
    contextFunctionsRef.current.providersDashboardData = globalContext.providersDashboardData;
  }, [
    globalContext.setProvidersData,
    globalContext.setSelectedProviderId,
    globalContext.setProvidersDashboardData,
    globalContext.providersData,
    globalContext.selectedProviderId,
    globalContext.providersDashboardData,
  ]);

  const fetchProviders = useCallback(async (entId, isManualRefresh = false, isAutoRefresh = false, searchQuery = '') => {
    // Validar que entId exista antes de hacer la petición
    if (!entId) {
      const error = new Error('entId es requerido para obtener proveedores');
      console.error('Error en fetchProviders:', error);
      setError(error);
      setLoading(false);
      setIsRefreshing(false);
      setIsAutoRefreshing(false);
      setIsSearching(false);
      throw error;
    }
    
    // Generar un ID único para este request
    const requestId = ++latestRequestIdRef.current;
    console.log(`=== INICIANDO FETCH PROVIDERS (OPTIMIZADO) ===`);
    console.log(`Request ID: ${requestId}`);
    console.log(`Manual: ${isManualRefresh}, Auto: ${isAutoRefresh}`);
    console.log(`Enterprise ID: ${entId}`);
    console.log(`Búsqueda: ${searchQuery}`);
    
    // Establecer estado de carga apropiado y rastrear cuál se estableció para este request
    // Dar precedencia a refresh manual/auto sobre búsquedas para mostrar el indicador correcto
    let loadingStateType = null;
    if (isManualRefresh) {
      setIsRefreshing(true);
      loadingStateType = 'isRefreshing';
      // Asegurar que otros estados estén en false cuando se hace refresh manual
      const hasSearchInProgress = Array.from(requestLoadingStateRef.current.values()).includes('isSearching');
      if (!hasSearchInProgress) {
        setIsSearching(false);
      }
    } else if (isAutoRefresh) {
      setIsAutoRefreshing(true);
      loadingStateType = 'isAutoRefreshing';
      // Asegurar que otros estados estén en false cuando se hace refresh auto
      const hasSearchInProgress = Array.from(requestLoadingStateRef.current.values()).includes('isSearching');
      if (!hasSearchInProgress) {
        setIsSearching(false);
      }
    } else if (searchQuery) {
      setIsSearching(true);
      loadingStateType = 'isSearching';
    } else {
      // Si no hay búsqueda ni refresh, solo establecer isSearching en false si no hay búsquedas en progreso
      // Esto previene que se limpie prematuramente el estado cuando hay búsquedas pendientes
      const hasSearchInProgress = Array.from(requestLoadingStateRef.current.values()).includes('isSearching');
      if (!hasSearchInProgress) {
        setIsSearching(false);
      }
      setLoading(true);
      loadingStateType = 'loading';
    }
    // Guardar qué estado de carga estableció este request
    requestLoadingStateRef.current.set(requestId, loadingStateType);
    setError(null);
    
    try {
      // Obtener proveedores del usuario con límite de 5 y búsqueda opcional
      const userId = localStorage.getItem('_id');
      
      // Validar que userId exista
      if (!userId) {
        throw new Error('Usuario no autenticado. Por favor, inicie sesión nuevamente.');
      }
      
      console.log('=== OBTENIENDO PROVEEDORES DEL USUARIO (LÍMITE: 5) ===');
      console.log('User ID:', userId);
      
      const mySuppliersResponse = await getMySuppliers(entId, userId, {
        limit: 5,
        offset: 0,
        searchText: searchQuery
      });
      
      // Validar que la respuesta sea válida
      if (!mySuppliersResponse || !mySuppliersResponse.data) {
        throw new Error('Respuesta inválida del servidor al obtener proveedores');
      }
      
      console.log('Respuesta my-suppliers:', mySuppliersResponse);
      
      const suppliers = mySuppliersResponse.data.data?.suppliers || [];
      console.log('Proveedores encontrados:', suppliers.length);
      
      // Mapear proveedores sin cargar totales (los totales se cargan al seleccionar)
      console.log('=== MAPEANDO PROVEEDORES SIN CARGAR TOTALES ===');
      const providersData = suppliers.map(supplier => ({
        ...supplier,
        ownerId: supplier.odooId,
        ownerName: supplier.name,
      }));
      
      console.log('=== DATOS FINALES (SIN TOTALES) ===');
      console.log('Providers data inicial:', providersData);
      console.log('Cantidad de providers inicial:', providersData.length);
      
      // Si hay un provider seleccionado y no está en la lista (y no hay búsqueda activa),
      // agregarlo para asegurar que siempre esté visible
      const currentSelectedProviderId = contextFunctionsRef.current.selectedProviderId;
      let finalProvidersData = providersData;
      
      if (currentSelectedProviderId && !searchQuery) {
        console.log('Provider ya seleccionado:', currentSelectedProviderId);
        const selectedProviderInList = providersData.find(
          provider => provider._id === currentSelectedProviderId
        );
        
        if (!selectedProviderInList) {
          console.log('Provider seleccionado no está en la lista, agregándolo...');
          // Intentar obtener la información del proveedor seleccionado desde providersDashboardData
          const providersDashboardData = contextFunctionsRef.current.providersDashboardData;
          
          if (providersDashboardData && providersDashboardData._id === currentSelectedProviderId) {
            // Usar los datos del dashboard para agregar el proveedor a la lista
            const providerToAdd = {
              ...providersDashboardData,
              // Asegurar que los campos básicos estén presentes
              _id: providersDashboardData._id,
              ownerId: providersDashboardData.ownerId || providersDashboardData.odooId,
              ownerName: providersDashboardData.ownerName || providersDashboardData.name,
              name: providersDashboardData.name || providersDashboardData.ownerName,
              odooId: providersDashboardData.odooId || providersDashboardData.ownerId,
              consignment: providersDashboardData.consignment,
            };
            
            // Agregar al inicio de la lista para que sea visible
            finalProvidersData = [providerToAdd, ...providersData];
            console.log('Provider seleccionado agregado a la lista');
          } else {
            // Si no tenemos los datos del dashboard, intentar desde localStorage
            try {
              const savedProviders = JSON.parse(localStorage.getItem('selectedProviders') || '[]');
              if (savedProviders.length > 0 && savedProviders[0]._id === currentSelectedProviderId) {
                const savedProvider = savedProviders[0];
                // Hacer una búsqueda específica para obtener el proveedor completo
                const searchResponse = await getMySuppliers(entId, localStorage.getItem('_id'), {
                  limit: 1,
                  offset: 0,
                  searchText: savedProvider.ownerName || savedProvider.name || ''
                });
                
                const foundProvider = searchResponse.data.data.suppliers?.find(
                  p => p._id === currentSelectedProviderId
                );
                
                if (foundProvider) {
                  const providerToAdd = {
                    ...foundProvider,
                    ownerId: foundProvider.odooId,
                    ownerName: foundProvider.name,
                  };
                  finalProvidersData = [providerToAdd, ...providersData];
                  console.log('Provider seleccionado encontrado y agregado desde búsqueda');
                }
              }
            } catch (error) {
              console.error('Error intentando obtener proveedor seleccionado:', error);
            }
          }
        } else {
          console.log('Provider seleccionado encontrado en la lista');
          // Solo actualizar si es consignación y necesitamos refrescar los datos
          if (isManualRefresh && selectedProviderInList.consignment === true) {
            console.log('Refrescando datos del provider seleccionado');
            try {
              const providerDataResponse = await getProviderData(entId, selectedProviderInList.odooId);
              const updatedData = {
                ...selectedProviderInList,
                ...providerDataResponse.data.data.totals,
                _id: selectedProviderInList._id,
                ownerId: selectedProviderInList.odooId,
                ownerName: selectedProviderInList.name,
              };
              contextFunctionsRef.current.setProvidersDashboardData(updatedData);
            } catch (error) {
              console.error('Error refrescando datos del provider:', error);
            }
          }
        }
      }
      
      // Verificar si este request sigue siendo el más reciente antes de actualizar contexto y UI
      if (requestId !== latestRequestIdRef.current) {
        console.log(`=== REQUEST OBSOLETO IGNORADO ===`);
        console.log(`Request ID: ${requestId}, Latest: ${latestRequestIdRef.current}`);
        // No actualizar el contexto ni el estado de UI si este request ya no es el más reciente
        // Esto previene que datos stale sobrescriban resultados más recientes
        // Solo limpiar el estado de carga específico que este request estableció
        const loadingStateType = requestLoadingStateRef.current.get(requestId);
        if (loadingStateType) {
          if (loadingStateType === 'loading') {
            setLoading(false);
          } else if (loadingStateType === 'isRefreshing') {
            setIsRefreshing(false);
          } else if (loadingStateType === 'isAutoRefreshing') {
            setIsAutoRefreshing(false);
          } else if (loadingStateType === 'isSearching') {
            setIsSearching(false);
          }
          // Limpiar la entrada del mapa
          requestLoadingStateRef.current.delete(requestId);
        }
        // Retornar null en lugar de datos stale para indicar que el request fue cancelado
        return null;
      }
      
      // Actualizar contexto global usando refs estables (solo para requests recientes)
      // Esto asegura que contextFunctionsRef tenga datos actualizados y evita sobrescribir con datos stale
      console.log('=== ACTUALIZANDO CONTEXTO GLOBAL ===');
      console.log('Providers data final:', finalProvidersData);
      console.log('Cantidad de providers final:', finalProvidersData.length);
      contextFunctionsRef.current.setProvidersData(finalProvidersData);
      
      // Actualizar timestamp de última actualización
      const newLastUpdate = new Date();
      setLastUpdate(newLastUpdate);
      console.log('Timestamp actualizado:', newLastUpdate);
      
      // Forzar re-render de componentes
      setForceUpdate(prev => prev + 1);
      console.log('Force update incrementado');
      
      // Limpiar solo el estado de carga que este request estableció
      // Obtener el tipo de estado ANTES de eliminarlo del mapa
      const loadingStateType = requestLoadingStateRef.current.get(requestId);
      if (loadingStateType) {
        if (loadingStateType === 'loading') {
          setLoading(false);
        } else if (loadingStateType === 'isRefreshing') {
          setIsRefreshing(false);
        } else if (loadingStateType === 'isAutoRefreshing') {
          setIsAutoRefreshing(false);
        } else if (loadingStateType === 'isSearching') {
          setIsSearching(false);
        }
        // Limpiar la entrada del mapa DESPUÉS de haber usado la información
        requestLoadingStateRef.current.delete(requestId);
      }
      
      console.log('=== FETCH COMPLETADO (OPTIMIZADO) ===');
      return finalProvidersData;
    } catch (err) {
      // Verificar si este request sigue siendo el más reciente antes de actualizar el error
      if (requestId !== latestRequestIdRef.current) {
        console.log(`=== ERROR EN REQUEST OBSOLETO IGNORADO ===`);
        console.log(`Request ID: ${requestId}, Latest: ${latestRequestIdRef.current}`);
        // Resetear solo el estado de carga específico que este request estableció
        const loadingStateType = requestLoadingStateRef.current.get(requestId);
        if (loadingStateType) {
          if (loadingStateType === 'loading') {
            setLoading(false);
          } else if (loadingStateType === 'isRefreshing') {
            setIsRefreshing(false);
          } else if (loadingStateType === 'isAutoRefreshing') {
            setIsAutoRefreshing(false);
          } else if (loadingStateType === 'isSearching') {
            setIsSearching(false);
          }
          // Limpiar la entrada del mapa
          requestLoadingStateRef.current.delete(requestId);
        }
        return;
      }
      
      console.error('=== ERROR EN FETCH ===');
      console.error('Error detallado:', err);
      
      // Crear un error más descriptivo para el usuario
      const userFriendlyError = err?.response?.data?.message || err?.message || 'Error al cargar los proveedores. Por favor, intente nuevamente.';
      const enhancedError = new Error(userFriendlyError);
      enhancedError.originalError = err;
      
      setError(enhancedError);
      
      // Limpiar solo el estado de carga que este request estableció
      const loadingStateType = requestLoadingStateRef.current.get(requestId);
      if (loadingStateType) {
        if (loadingStateType === 'loading') {
          setLoading(false);
        } else if (loadingStateType === 'isRefreshing') {
          setIsRefreshing(false);
        } else if (loadingStateType === 'isAutoRefreshing') {
          setIsAutoRefreshing(false);
        } else if (loadingStateType === 'isSearching') {
          setIsSearching(false);
        }
        // Limpiar la entrada del mapa
        requestLoadingStateRef.current.delete(requestId);
      }
      
      // Lanzar el error para que los componentes puedan manejarlo
      throw enhancedError;
    }
  }, []); // Sin dependencias - usa refs estables en lugar del contexto completo

  // Función para actualización manual
  // Usa searchTextRef para evitar dependencia de searchText que cambia frecuentemente
  const searchTextRef = useRef('');
  useEffect(() => {
    searchTextRef.current = searchText;
  }, [searchText]);
  
  const refreshProviders = useCallback(async () => {
    console.log('=== ACTUALIZACIÓN MANUAL INICIADA ===');
    try {
      await fetchProviders(currentEntIdRef.current, true, false, searchTextRef.current);
      console.log('=== ACTUALIZACIÓN MANUAL COMPLETADA ===');
    } catch (error) {
      console.error('Error en actualización manual:', error);
    }
  }, [fetchProviders]); // Solo depende de fetchProviders que ahora es estable

  // Función de búsqueda con debounce
  const searchProviders = useCallback((query) => {
    console.log('=== BÚSQUEDA DE PROVEEDORES ===');
    console.log('Query:', query);
    
    // Actualizar el estado de búsqueda
    setSearchText(query);
    searchTextRef.current = query;
    
    // Limpiar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Si la búsqueda está vacía, cargar proveedores normales inmediatamente
    if (!query || !query.trim()) {
      console.log('Búsqueda vacía, cargando proveedores iniciales');
      fetchProviders(currentEntIdRef.current, false, false, '');
      return;
    }
    
    // Aplicar debounce de 500ms para búsquedas
    searchTimeoutRef.current = setTimeout(() => {
      console.log('Ejecutando búsqueda después de debounce');
      fetchProviders(currentEntIdRef.current, false, false, query);
    }, 500);
  }, [fetchProviders]);

  // // Efecto para configurar la actualización periódica SOLO UNA VEZ
  // useEffect(() => {
  //   // Solo configurar si no se ha configurado antes Y hay datos
  //   if (!hasConfiguredInterval.current && globalContext.providersData && globalContext.providersData.length > 0) {
  //     console.log('=== CONFIGURANDO ACTUALIZACIÓN PERIÓDICA ===');
  //     hasConfiguredInterval.current = true;
      
  //     intervalRef.current = setInterval(() => {
  //       console.log('=== EJECUTANDO ACTUALIZACIÓN PERIÓDICA === 0000000000000');
  //       fetchProviders(currentEntIdRef.current, false, true);
  //     }, 2 * 60 * 1000); // 2 minutos en milisegundos
  //   }

  //   // Cleanup al desmontar componente (no en cada render)
  //   return () => {
  //     if (intervalRef.current) {
  //       console.log('=== LIMPIANDO INTERVAL ===');
  //       clearInterval(intervalRef.current);
  //       hasConfiguredInterval.current = false;
  //     }
  //   };
  // }, []); // Dependencias vacías para que solo se ejecute al montar/desmontar

  // Efecto separado para detectar cuando se cargan datos por primera vez
  // useEffect(() => {
  //   if (!hasConfiguredInterval.current && globalContext.providersData && globalContext.providersData.length > 0) {
  //     console.log('=== DATOS DETECTADOS - CONFIGURANDO ACTUALIZACIÓN PERIÓDICA ===');
  //     hasConfiguredInterval.current = true;
      
  //     intervalRef.current = setInterval(() => {
  //       console.log('=== EJECUTANDO ACTUALIZACIÓN PERIÓDICA === 11111111111111');
  //       fetchProviders(currentEntIdRef.current, false, true);
  //     }, 2 * 60 * 1000);
  //   }
  // }, [globalContext.providersData?.length, fetchProviders]); // Solo cuando cambie la longitud de los datos

  // Efecto para limpiar timeouts e intervals cuando el hook se desmonta
  useEffect(() => {
    return () => {
      // Limpiar timeout de búsqueda pendiente
      if (searchTimeoutRef.current) {
        console.log('=== LIMPIANDO TIMEOUT DE BÚSQUEDA ===');
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
      
      // Limpiar interval si existe
      if (intervalRef.current) {
        console.log('=== LIMPIANDO INTERVAL ===');
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        hasConfiguredInterval.current = false;
      }
    };
  }, []); // Solo se ejecuta al montar/desmontar

  // Efecto para sincronizar el proveedor seleccionado del localStorage con el contexto global
  useEffect(() => {
    if (globalContext.providersData && globalContext.providersData.length > 0 && !globalContext.selectedProviderId) {
      // Recuperar el proveedor seleccionado del localStorage
      const savedProviders = JSON.parse(localStorage.getItem('selectedProviders') || '[]');
      if (savedProviders.length > 0) {
        const lastSelectedProvider = savedProviders[0];
        const providerExists = globalContext.providersData.find(
          provider => provider._id === lastSelectedProvider._id
        );
        
        if (providerExists) {
          console.log('=== SINCRONIZANDO PROVIDER DEL LOCALSTORAGE ===');
          console.log('Provider encontrado:', lastSelectedProvider.ownerName);
          globalContext.setSelectedProviderId(lastSelectedProvider._id);
          globalContext.setProvidersDashboardData(providerExists);
        }
      }
    }
  }, [globalContext.providersData?.length]); // Solo cuando cambie la cantidad de providers

  const selectProvider = async (providerId) => {
    console.log('=== SELECCIONANDO PROVIDER ===');
    console.log('Provider ID:', providerId);
    
    // Resetear búsqueda cuando se selecciona un proveedor
    setSearchText('');
    searchTextRef.current = '';
    
    // Limpiar timeout de búsqueda si existe
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    
    contextFunctionsRef.current.setSelectedProviderId(providerId);
    
    // Buscar el proveedor seleccionado
    const currentProvidersData = contextFunctionsRef.current.providersData || globalContext.providersData;
    const selectedProvider = currentProvidersData.find(
      provider => provider._id === providerId
    );
    
    console.log('Provider encontrado:', selectedProvider);
    
    if (selectedProvider) {
      console.log(`Proveedor seleccionado: ${selectedProvider.name}, consignment: ${selectedProvider.consignment}`);
      console.log(`Tipo de consignment: ${typeof selectedProvider.consignment}, Valor: ${selectedProvider.consignment}`);
      
      if (selectedProvider.consignment === true) {
        try {
          // Obtener datos actualizados del proveedor seleccionado (solo si es consignación)
          console.log('=== OBTENIENDO DATOS ACTUALIZADOS DEL PROVEEDOR (CONSIGNACIÓN) ===');
          const entId = currentEntIdRef.current;
          const providerDataResponse = await getProviderData(entId, selectedProvider.odooId);
          console.log('Respuesta completa del endpoint owner-totals:', providerDataResponse);
          console.log('Datos extraídos de la respuesta:', providerDataResponse.data.data);
          console.log('generalTotals en respuesta:', providerDataResponse.data.data.totals?.generalTotals);
          
          // Verificar estructura de la respuesta
          console.log('Estructura completa de la respuesta:', {
            hasData: !!providerDataResponse.data,
            hasDataData: !!providerDataResponse.data.data,
            hasTotals: !!providerDataResponse.data.data.totals,
            dataKeys: providerDataResponse.data.data ? Object.keys(providerDataResponse.data.data) : [],
            totalsKeys: providerDataResponse.data.data.totals ? Object.keys(providerDataResponse.data.data.totals) : [],
            hasGeneralTotals: !!providerDataResponse.data.data.totals?.generalTotals,
            generalTotalsKeys: providerDataResponse.data.data.totals?.generalTotals ? Object.keys(providerDataResponse.data.data.totals.generalTotals) : []
          });
          
          // Combinar datos del proveedor con sus datos actualizados de dashboard
          const updatedProviderData = {
            ...selectedProvider, // Datos del proveedor (nombre, odooId, consignment, _id, etc.)
            ...providerDataResponse.data.data.totals, // Datos actualizados del dashboard (generalTotals, etc.)
            // Asegurar que los campos esperados por los componentes estén presentes
            _id: selectedProvider._id, // Preservar explícitamente el _id del proveedor
            ownerId: selectedProvider.odooId, // Mapear odooId a ownerId
            ownerName: selectedProvider.name, // Mapear name a ownerName
          };
          
          console.log('Datos actualizados del proveedor:', updatedProviderData);
          console.log('generalTotals en datos actualizados:', updatedProviderData.generalTotals);
          console.log('ownerId en datos actualizados:', updatedProviderData.ownerId);
          console.log('ownerName en datos actualizados:', updatedProviderData.ownerName);
          
        // Actualizar el contexto global con los datos actualizados
        contextFunctionsRef.current.setProvidersDashboardData(updatedProviderData);
        console.log('=== CONTEXTO GLOBAL ACTUALIZADO ===');
        console.log('Nuevos datos en contexto:', updatedProviderData);
        console.log('generalTotals en contexto:', updatedProviderData.generalTotals);
        
        } catch (error) {
          console.error('Error obteniendo datos actualizados del proveedor:', error);
          // En caso de error, usar los datos existentes con campos mapeados
          const fallbackData = {
            ...selectedProvider,
            ownerId: selectedProvider.odooId,
            ownerName: selectedProvider.name,
          };
          contextFunctionsRef.current.setProvidersDashboardData(fallbackData);
        }
      } else {
        console.log('Proveedor no es consignación, usando datos básicos');
        // Para proveedores sin consignación, usar solo los datos básicos con campos mapeados
        const basicData = {
          ...selectedProvider,
          ownerId: selectedProvider.odooId,
          ownerName: selectedProvider.name,
        };
        contextFunctionsRef.current.setProvidersDashboardData(basicData);
        console.log('=== CONTEXTO GLOBAL ACTUALIZADO (SIN CONSIGNACIÓN) ===');
        console.log('Datos básicos en contexto:', basicData);
      }
      
      // Guardar en localStorage para persistencia (para ambos casos)
      const savedProviders = [{
        _id: selectedProvider._id,
        ownerId: selectedProvider.odooId, // Usar odooId como ownerId
        ownerName: selectedProvider.name,
        consignment: selectedProvider.consignment
      }];
      localStorage.setItem('selectedProviders', JSON.stringify(savedProviders));
      console.log('Provider guardado en localStorage:', savedProviders);
      
      // Forzar re-render de componentes
      setForceUpdate(prev => prev + 1);
    }
  };

  // Formatear la última actualización
  const getLastUpdateFormatted = () => {
    if (!lastUpdate) return null;
    
    return lastUpdate.toLocaleString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      day: '2-digit',
      month: '2-digit',
      timeZone: 'America/Caracas'
    });
  };

  // Debug: Log del estado actual
  useEffect(() => {
    console.log('=== ESTADO ACTUAL DEL HOOK ===');
    console.log('ProvidersDashboardData:', globalContext.providersDashboardData);
    console.log('generalTotals:', globalContext.providersDashboardData?.generalTotals);
    console.log('ownerId:', globalContext.providersDashboardData?.ownerId);
    console.log('ownerName:', globalContext.providersDashboardData?.ownerName);
  }, [loading, isRefreshing, isAutoRefreshing, globalContext.providersData, globalContext.selectedProviderId, globalContext.providersDashboardData, forceUpdate]);

  // Efecto para detectar cambios en providersDashboardData y forzar re-render
  useEffect(() => {
    if (globalContext.providersDashboardData) {
      console.log('=== CAMBIO DETECTADO EN PROVIDERS DASHBOARD DATA ===');
      console.log('Nuevos datos:', globalContext.providersDashboardData);
      console.log('generalTotals:', globalContext.providersDashboardData.generalTotals);
      console.log('ownerId:', globalContext.providersDashboardData.ownerId);
      console.log('ownerName:', globalContext.providersDashboardData.ownerName);
      setForceUpdate(prev => prev + 1);
      console.log('Force update incrementado');
    }
  }, [globalContext.providersDashboardData]);

  return {
    // Estados
    providersData: globalContext.providersData,
    selectedProviderId: globalContext.selectedProviderId,
    providersDashboardData: globalContext.providersDashboardData,
    loading,
    error,
    lastUpdate,
    isRefreshing,
    isAutoRefreshing,
    isSearching,
    searchText,
    forceUpdate, // Para forzar re-renders
    
    // Métodos
    fetchProviders,
    refreshProviders,
    selectProvider,
    searchProviders,
    getLastUpdateFormatted,
    setProvidersData: globalContext.setProvidersData,
    setSelectedProviderId: globalContext.setSelectedProviderId,
    setProvidersDashboardData: globalContext.setProvidersDashboardData,
    
    // Nuevas funciones exportadas
    getMySuppliers,
    getProviderData,
    getProviders, // Función original mantenida para compatibilidad
  };
};




